import * as THREE from 'three';
import { Entity, type EntityCtx } from '../Entity';
import { Explosion } from './Explosion';
import { spawnWreckage, WRECK_PALETTES } from './Wreckage';
import { Shell } from './Shell';
import { useStore } from '../../state/store';
import type { Building, Village } from '../Scene';

/**
 * WW1-era heavy tank. Burrows up at the world edge, drives across the terrain toward
 * the nearest live village, stops within firing range, and bombards the village
 * building-by-building until it's flattened. Then moves to the next live village.
 *
 * Killed by player MG strafing (4 bullets). Visible on the radar in purple.
 */
const HP = 4;
const SPEED = 14;          // u/s on the ground
const FIRE_RANGE = 360;
const FIRE_INTERVAL = 2.6;
const SHELL_SPEED = 600;   // matches the allied tank's shell so rounds read consistently
const BURROW_DURATION = 1.8;
const GROUND_Y = -468;     // top of terrain — tank treads sit here
const SCORE = 100;

type Stage = 'burrowing' | 'driving' | 'bombarding' | 'dying' | 'wreck';

export class Tank extends Entity {
  stage: Stage = 'burrowing';
  burrowT = 0;
  fireCooldown = 1.5;
  forward = new THREE.Vector3(0, 0, -1);
  /** Speed exposed for parity with other entities; tanks are silent on engine audio for now. */
  readonly speed = SPEED;
  private dustTimer = 0;
  deathTimer = 0;
  wreckTimer = 0;
  private currentTarget: Village | null = null;
  private turret: THREE.Object3D | null = null;
  private cannon: THREE.Object3D | null = null;

  constructor(pos: THREE.Vector3) {
    super('tank');
    this.hp = HP;
    this.radius = 9;
    this.group.position.copy(pos);
    this.group.position.y = GROUND_Y - 8; // start buried
    const mesh = makeTankMesh();
    this.turret = mesh.userData.turret as THREE.Object3D;
    this.cannon = mesh.userData.cannon as THREE.Object3D;
    this.group.add(mesh);

    // Per-component collision: hull body, traversable turret + cannon, and each track.
    // Any part hit calls Entity.damagePart() which falls through to damage() — the tank
    // dies after HP rounds regardless of where it gets shot. Splitting it like this means
    // bullets that pass between the tracks/turret/hull don't get magically eaten by a
    // single big AABB.
    this.hitParts = [
      // Main hull (lower body + sloped glacis + upper deck — they overlap into one box).
      { id: 'hull',
        box: new THREE.Box3(new THREE.Vector3(-4.2, -0.4, -7.0), new THREE.Vector3(4.2, 4.5, 7.0)),
        object: this.group },
      // Turret (rotates with this.turret) — covers turret body + cupola + the long cannon barrel.
      { id: 'turret',
        box: new THREE.Box3(new THREE.Vector3(-2.8, -1.5, -6.0), new THREE.Vector3(2.8, 1.5, 2.8)),
        object: this.turret },
      // Left track — exposed below/outside the hull line.
      { id: 'trackL',
        box: new THREE.Box3(new THREE.Vector3(-4.9, -0.8, -7.2), new THREE.Vector3(-3.1, 2.9, 7.2)),
        object: this.group },
      // Right track.
      { id: 'trackR',
        box: new THREE.Box3(new THREE.Vector3(3.1, -0.8, -7.2), new THREE.Vector3(4.9, 2.9, 7.2)),
        object: this.group },
    ];
  }

  alive(): boolean { return this.stage !== 'dying' && this.stage !== 'wreck'; }

  damage(amount: number, ctx: EntityCtx): void {
    if (this.stage === 'dying' || this.stage === 'wreck') return;
    this.hp -= amount;
    if (this.hp <= 0) this.beginDying(ctx);
  }

  update(ctx: EntityCtx): void {
    const { dt } = ctx;
    // Death + wreck stages first
    if (this.stage === 'dying') {
      this.deathTimer += dt;
      if (this.deathTimer < 0.6) {
        if (Math.random() < 0.6) {
          const off = new THREE.Vector3((Math.random() - 0.5) * 6, 1, (Math.random() - 0.5) * 6);
          new Explosion(this.group.position.clone().add(off), 14, 0.5, 'fire').addTo(ctx);
        }
      } else {
        this.stage = 'wreck';
        this.wreckTimer = 0;
      }
      return;
    }
    if (this.stage === 'wreck') {
      this.wreckTimer += dt;
      if (this.wreckTimer < 16) {
        if (Math.random() < 0.12) {
          const off = new THREE.Vector3((Math.random() - 0.5) * 4, 1, (Math.random() - 0.5) * 4);
          new Explosion(this.group.position.clone().add(off), 12, 3.5, 'smoke')
            .setDrift(new THREE.Vector3(0, 8, 0)).addTo(ctx);
        }
      } else {
        this.dead = true;
      }
      return;
    }
    const gy = ctx.game.gameScene.heightAt(this.group.position.x, this.group.position.z);
    if (this.stage === 'burrowing') {
      this.burrowT += dt;
      // Rise from -8 below ground to the terrain surface over BURROW_DURATION.
      const t = Math.min(1, this.burrowT / BURROW_DURATION);
      this.group.position.y = gy - 8 + t * 8;
      // Dust puffs while emerging
      this.dustTimer += dt;
      if (this.dustTimer >= 0.06) {
        this.dustTimer = 0;
        const off = new THREE.Vector3((Math.random() - 0.5) * 8, 0.5, (Math.random() - 0.5) * 8);
        new Explosion(this.group.position.clone().add(off), 8, 1.6, 'smoke')
          .setDrift(new THREE.Vector3(0, 4, 0))
          .addTo(ctx);
      }
      if (t >= 1) {
        this.group.position.y = gy;
        this.stage = 'driving';
        this.pickTarget(ctx);
      }
      return;
    }
    // Stay seated on the rolling terrain as it drives.
    this.group.position.y = gy;

    // ARMOUR DUEL: if an allied ground unit (tank or AA/trench gun) is within cannon range, engage
    // IT first — halt, traverse onto it, and shell it — rather than rolling past to the village.
    const ally = ctx.game.nearestAllyGround(this.group.position, FIRE_RANGE);
    if (ally) {
      this.engageTargetId = ally.id;
      this.aimTurret(ally.position);
      this.fireCooldown -= dt;
      if (!this.trainingTarget && this.fireCooldown <= 0) {
        this.fireCooldown = FIRE_INTERVAL;
        this.fireAtUnit(ctx, ally);
      }
      return; // hold position and duel; don't advance toward the village this frame
    }

    // Re-target if village got destroyed by another threat or this tank
    if (!this.currentTarget || this.currentTarget.destroyed) this.pickTarget(ctx);
    if (!this.currentTarget) {
      // No villages left — sit and idle (player can still kill it)
      return;
    }

    const target = this.currentTarget;
    // Flat horizontal vector from tank to village center.
    const toTarget = target.position.clone().sub(this.group.position);
    toTarget.y = 0;
    const dist = toTarget.length();
    if (dist > 0.001) toTarget.multiplyScalar(1 / dist);
    // Smoothly steer the tank's forward toward the target.
    this.forward.lerp(toTarget, 0.04).normalize();
    // Yaw the visual hull to match the heading.
    const yaw = Math.atan2(this.forward.x, this.forward.z);
    this.group.rotation.set(0, yaw, 0);

    if (this.stage === 'driving') {
      // Trenches impede the advance: a tank astride the parapet crawls and is left exposed.
      const slow = ctx.game.gameScene.trenchSlowFactor(this.group.position.x, this.group.position.z);
      this.group.position.addScaledVector(this.forward, SPEED * dt * slow);
      if (slow < 0.5) {
        // Struggling across the wire — throw spoil so the hold-up reads.
        this.dustTimer += dt;
        if (this.dustTimer >= 0.12) {
          this.dustTimer = 0;
          const off = new THREE.Vector3((Math.random() - 0.5) * 5, 0.4, (Math.random() - 0.5) * 5);
          new Explosion(this.group.position.clone().add(off), 6, 1.1, 'smoke')
            .setDrift(new THREE.Vector3(0, 3, 0)).addTo(ctx);
        }
      }
      // Reach firing range → bombard.
      if (dist <= FIRE_RANGE) {
        this.stage = 'bombarding';
      }
    } else if (this.stage === 'bombarding') {
      // Tank moved (or village was bombed by someone else) — chase.
      if (dist > FIRE_RANGE * 1.15) {
        this.stage = 'driving';
        return;
      }
      // Aim turret at the target, fire shells on cooldown.
      this.aimTurret(target.position);
      this.fireCooldown -= dt;
      if (!this.trainingTarget && this.fireCooldown <= 0) {
        this.fireCooldown = FIRE_INTERVAL;
        this.fireShell(ctx, target);
      }
    }
  }

  private aimTurret(targetWorld: THREE.Vector3) {
    if (!this.turret) return;
    // Convert target into hull-local space, set turret yaw to point at it.
    const local = targetWorld.clone();
    this.group.updateWorldMatrix(true, false);
    local.applyMatrix4(new THREE.Matrix4().copy(this.group.matrixWorld).invert());
    const yaw = Math.atan2(local.x, -local.z);
    this.turret.rotation.set(0, yaw, 0);
  }

  private fireShell(ctx: EntityCtx, target: Village) {
    // Aim at a random live building so artillery destroys structures one at a time. The flying
    // shell craters whatever building it strikes (hitsBuildings), so the village degrades.
    const livePool: Building[] = target.buildings.filter((b) => !b.destroyed);
    if (livePool.length === 0) return;
    const victim = livePool[Math.floor(Math.random() * livePool.length)];
    const aim = victim.position.clone().setY(victim.position.y + 4);
    const shell = this.launchShell(ctx, aim);
    if (shell) shell.hitsBuildings = true;
  }

  /** Shell a friendly ground unit with a real flying round (its impact does the damage). */
  private fireAtUnit(ctx: EntityCtx, victim: Entity) {
    this.launchShell(ctx, victim.position.clone().setY(victim.position.y + 1.5));
  }

  /** Muzzle flash + boom, then a real flying enemy Shell from the cannon tip toward `aim`. */
  private launchShell(ctx: EntityCtx, aim: THREE.Vector3): Shell | null {
    const muzzle = new THREE.Vector3();
    if (!this.cannon) return null;
    this.cannon.getWorldPosition(muzzle);
    const dir = aim.clone().sub(muzzle);
    if (dir.lengthSq() < 1e-6) return null;
    dir.normalize();
    muzzle.addScaledVector(dir, 2.4); // spawn at the barrel tip, not mid-cannon
    new Explosion(muzzle.clone(), 9, 0.18, 'fire').addTo(ctx);
    new Explosion(muzzle.clone(), 6, 0.6, 'smoke').setDrift(new THREE.Vector3(0, 4, 0)).addTo(ctx);
    ctx.game.audio.playBoom(0.45);
    const shell = new Shell(muzzle.clone(), dir.multiplyScalar(SHELL_SPEED), this.id, 'enemy');
    ctx.game.add(shell);
    return shell;
  }

  private pickTarget(ctx: EntityCtx) {
    let best: Village | null = null;
    let bestD = Infinity;
    for (const v of ctx.game.gameScene.villages) {
      if (v.destroyed) continue;
      const dx = v.position.x - this.group.position.x;
      const dz = v.position.z - this.group.position.z;
      const d = Math.hypot(dx, dz);
      if (d < bestD) { bestD = d; best = v; }
    }
    this.currentTarget = best;
  }

  private beginDying(ctx: EntityCtx) {
    useStore.getState().registerKill(SCORE);
    // Blow apart into tumbling armour, tracks and wheels that cook off across the ground.
    spawnWreckage(ctx, this.group.position.clone(), 'tank', WRECK_PALETTES.enemyTank,
      { baseVel: this.forward.clone().multiplyScalar(this.speed), count: 8, boom: 1.0 });
    this.dead = true;
  }

}

export function makeTankMesh(palette?: { armor: number; armorDark: number }): THREE.Group {
  const g = new THREE.Group();
  const armorCol = palette?.armor ?? 0x3e4a36;
  const armorDarkCol = palette?.armorDark ?? 0x2a3324;
  const armor = new THREE.MeshStandardMaterial({ color: armorCol, roughness: 0.95, flatShading: true });
  const armorDark = new THREE.MeshStandardMaterial({ color: armorDarkCol, roughness: 0.95, flatShading: true });
  const tread = new THREE.MeshStandardMaterial({ color: 0x141414, roughness: 0.95 });
  const metal = new THREE.MeshStandardMaterial({ color: 0x2a2a2c, roughness: 0.45, metalness: 0.7 });

  // Hull (rhomboid-ish — two stepped boxes that suggest a Mark IV)
  const hullMain = new THREE.Mesh(new THREE.BoxGeometry(8, 3, 13, 4, 4, 8), armor);
  hullMain.position.set(0, 1.5, 0);
  g.add(hullMain);
  // Sloped top
  const hullTop = new THREE.Mesh(new THREE.BoxGeometry(7, 1.4, 9, 4, 2, 6), armorDark);
  hullTop.position.set(0, 3.4, 0);
  g.add(hullTop);
  // Front glacis (sloped front plate)
  const glacis = new THREE.Mesh(new THREE.BoxGeometry(8, 2.2, 0.5, 4, 4, 1), armorDark);
  glacis.rotation.x = -0.4;
  glacis.position.set(0, 1.6, -6.3);
  g.add(glacis);

  // Tracks — boxes running along each side, sticking out below and at front/rear
  for (const sx of [-4, 4]) {
    const trackBody = new THREE.Mesh(new THREE.BoxGeometry(1.2, 3.2, 14, 1, 4, 12), tread);
    trackBody.position.set(sx, 1.0, 0);
    g.add(trackBody);
    // Lugged drive sprockets (visible as small wheels at ends)
    for (const sz of [-6.4, 6.4]) {
      const sprocket = new THREE.Mesh(new THREE.CylinderGeometry(1.0, 1.0, 1.4, 24), tread);
      sprocket.rotation.z = Math.PI / 2;
      sprocket.position.set(sx, 1.0, sz);
      g.add(sprocket);
    }
    // Road wheels
    for (let i = -2; i <= 2; i++) {
      const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.7, 1.2, 20), tread);
      wheel.rotation.z = Math.PI / 2;
      wheel.position.set(sx, 0.5, i * 2.5);
      g.add(wheel);
    }
  }

  // Turret: short stubby cylinder on the hull top, slightly forward.
  const turret = new THREE.Group();
  turret.position.set(0, 4.2, -0.5);
  g.add(turret);
  const turretBody = new THREE.Mesh(new THREE.CylinderGeometry(1.4, 1.6, 1.4, 24, 2), armor);
  turret.add(turretBody);
  // Cupola
  const cupola = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.45, 0.55, 16), armorDark);
  cupola.position.y = 0.95;
  turret.add(cupola);
  // Cannon — protrudes forward
  const cannon = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.2, 4.5, 24), metal);
  cannon.rotation.x = Math.PI / 2;
  cannon.position.set(0, 0.05, -3.0);
  turret.add(cannon);
  // Mantlet collar
  const collar = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.32, 0.45, 16), armorDark);
  collar.rotation.x = Math.PI / 2;
  collar.position.set(0, 0.05, -1.0);
  turret.add(collar);

  // Side sponsons (machine-gun blisters jutting out — Mark IV signature)
  for (const sx of [-4.2, 4.2]) {
    const blister = new THREE.Mesh(new THREE.BoxGeometry(2.2, 1.4, 3.0, 2, 2, 4), armor);
    blister.position.set(sx * 1.05, 1.7, 0);
    g.add(blister);
    const mgBarrel = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 1.4, 12), metal);
    mgBarrel.rotation.z = Math.PI / 2;
    mgBarrel.position.set(sx * 1.5, 1.7, 0);
    g.add(mgBarrel);
  }

  g.userData.turret = turret;
  g.userData.cannon = cannon;
  return g;
}
